import { useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, SlideOut, TextArea, useToast } from '@aimsos/ui';
import {
  buildApprovalDecision,
  parseCustomDecision,
  HitlDecisionJsonError,
  classifyHitlResolveError,
  markHitlContractDismissed,
} from '@aimsos/chat';

// Toast copy (chat-namespace keys) per resolve-error kind that dismisses the
// panel instead of keeping it open for a retry.
const DISMISS_COPY = {
  already_resolved: ['chat.hitl.alreadyResolved', 'chat.hitl.alreadyResolvedDetail'],
  resume_conflict: ['chat.hitl.resumeConflict', 'chat.hitl.resumeConflictDetail'],
};

/**
 * Decision panel for one HITL task.
 *
 * Two modes, chosen by the task itself:
 *
 * - **Approve / Reject** (`render.ui_block === 'hitl_approval'`): comments
 *   textarea + two actions. The decision sent is
 *   `{ gate, status, comments }` (plus `rejection_type: 'minor'` on reject —
 *   the strategy gates read it; gates that don't simply ignore extras).
 * - **Custom contract** (everything else, e.g. a brief-review or document
 *   pick): a JSON editor prefilled from `resumeMatch`, because each gate
 *   defines its own resume keys and `render.summary` documents them.
 *
 * The render blob is engine-authored and untyped — every scalar field is
 * shown as a labelled row so the human sees what they are deciding on.
 */
const TaskDecisionSlideOut = ({ task, isOpen, onClose, onResolve, resolving }) => {
  const { t } = useTranslation();
  const { t: tChat } = useTranslation('chat');
  const toast = useToast();
  const [comments, setComments] = useState('');
  const [customDecision, setCustomDecision] = useState(() =>
    JSON.stringify({ ...(task?.resumeMatch ?? {}) }, null, 2),
  );

  const render = task?.render ?? {};
  const gate = task?.resumeMatch?.gate;
  const isApproval = render.ui_block === 'hitl_approval';

  const detailRows = useMemo(
    () =>
      Object.entries(render).filter(
        ([key, value]) =>
          key !== 'ui_block' && (typeof value === 'string' ? value.length : value != null),
      ),
    [render],
  );

  const submit = async (decision) => {
    try {
      const result = await onResolve({ contractId: task.contractId, decision });
      // Resolved for real — deny re-seed of the in-chat durable card, then close.
      markHitlContractDismissed(task.contractId);
      onClose();
      if (result?.error_code) {
        toast.success({
          title: t('tasks.decision.recorded'),
          message: t('tasks.decision.recordedRejected'),
        });
      } else {
        toast.success({
          title: t('tasks.decision.sent'),
          message: t('tasks.decision.sentDetail'),
        });
      }
    } catch (err) {
      const { kind } = classifyHitlResolveError(err);
      if (DISMISS_COPY[kind]) {
        const [titleKey, messageKey] = DISMISS_COPY[kind];
        // Close first (and deny re-seed) so a throwing toast can never strand the panel.
        markHitlContractDismissed(task.contractId);
        onClose();
        toast.info({ title: tChat(titleKey), message: tChat(messageKey) });
        return;
      }
      toast.error({
        title: t('tasks.decision.failed'),
        message: err?.response?.data?.detail || err?.message || String(err),
      });
    }
  };

  const submitApproval = (status) =>
    submit(buildApprovalDecision({ gate, status, comments }));

  const submitCustom = () => {
    let decision;
    try {
      decision = parseCustomDecision(customDecision);
    } catch (err) {
      if (err instanceof HitlDecisionJsonError) {
        toast.warning({
          title: t('tasks.decision.invalidJson'),
          message: t('tasks.decision.invalidJsonDetail'),
        });
        return;
      }
      throw err;
    }
    return submit(decision);
  };

  if (!task) return null;

  return (
    <SlideOut isOpen={isOpen} onClose={onClose} size="M">
      <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
        <div className="flex items-center gap-2">
          <Badge>{task.hitlType}</Badge>
          <Badge>{task.nodeId}</Badge>
        </div>

        <p className="text-sm whitespace-pre-wrap">{render.summary}</p>

        {detailRows
          .filter(([key]) => key !== 'summary')
          .map(([key, value]) => (
            <div key={key}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60">{key}</p>
              <p className="text-sm whitespace-pre-wrap break-words">
                {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
              </p>
            </div>
          ))}

        {isApproval ? (
          <>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">
                {t('tasks.decision.comments')}
              </p>
              <TextArea
                value={comments}
                onChange={(event) => setComments(event.target.value)}
                placeholder={t('tasks.decision.commentsPlaceholder')}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => submitApproval('approved')} disabled={resolving}>
                {t('tasks.decision.approve')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => submitApproval('rejected')}
                disabled={resolving}
              >
                {t('tasks.decision.reject')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">
                {t('tasks.decision.customLabel')}
              </p>
              <TextArea
                value={customDecision}
                onChange={(event) => setCustomDecision(event.target.value)}
                rows={8}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={submitCustom} disabled={resolving}>
                {t('tasks.decision.send')}
              </Button>
            </div>
          </>
        )}
      </div>
    </SlideOut>
  );
};

export default TaskDecisionSlideOut;
